import { FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { SlideOver } from '@/components/SlideOver.js';
import {
  createSubscription,
  type CreateSubscriptionInput,
  type SubscriptionFrequency,
} from '@/lib/api/subscriptions.js';
import { listCustomers } from '@/lib/api/customers.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

const FREQUENCIES: Array<{ value: SubscriptionFrequency; label: string }> = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

export function NewSubscriptionSlideOver({
  open,
  onClose,
  onCreated,
  defaultCustomerId,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  defaultCustomerId?: string;
}) {
  const [customerId, setCustomerId] = useState(defaultCustomerId ?? '');
  const [customerSearch, setCustomerSearch] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [frequency, setFrequency] = useState<SubscriptionFrequency>('monthly');
  const [startDate, setStartDate] = useState(todayIsoDate());
  const [nextBillingDate, setNextBillingDate] = useState(todayIsoDate());
  const [autoCharge, setAutoCharge] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open) {
      setCustomerId(defaultCustomerId ?? '');
      setCustomerSearch('');
      setName('');
      setDescription('');
      setAmount('');
      setFrequency('monthly');
      const today = todayIsoDate();
      setStartDate(today);
      setNextBillingDate(today);
      setAutoCharge(false);
      setNotes('');
    }
  }, [open, defaultCustomerId]);

  const customersQuery = useQuery({
    queryKey: ['customers', customerSearch],
    queryFn: () => listCustomers({ q: customerSearch, page: 1, pageSize: 20 }),
    enabled: open && customerSearch.length >= 2,
  });

  const mutation = useMutation({
    mutationFn: (body: CreateSubscriptionInput) => createSubscription(body),
    onSuccess: () => {
      toastSuccess('Subscription created');
      onCreated();
      onClose();
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!customerId) {
      toastError('Select a customer');
      return;
    }
    if (!name.trim()) {
      toastError('Enter a subscription name');
      return;
    }
    const amountNum = Number(amount);
    if (!amountNum || amountNum <= 0) {
      toastError('Enter a valid amount');
      return;
    }
    mutation.mutate({
      customerId,
      name: name.trim(),
      description: description.trim() || undefined,
      amount: amountNum,
      frequency,
      startDate,
      nextBillingDate: nextBillingDate || startDate,
      autoCharge,
      notes: notes.trim() || undefined,
    });
  }

  return (
    <SlideOver title="New Subscription" open={open} onClose={onClose} panelClassName="max-w-lg">
      <form className="space-y-4" onSubmit={handleSubmit}>
        <div>
          <label className="block text-sm font-medium text-slate-700">Customer</label>
          {defaultCustomerId ? (
            <p className="mt-1 text-sm text-slate-600">Customer pre-selected</p>
          ) : (
            <>
              <input
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                placeholder="Search by name, phone, or email"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
              />
              {customersQuery.data?.data.length ? (
                <ul className="mt-2 max-h-40 overflow-auto rounded-xl border border-slate-200">
                  {customersQuery.data.data.map((c) => {
                    const label = [c.firstName, c.lastName].filter(Boolean).join(' ');
                    return (
                      <li key={c.id}>
                        <button
                          type="button"
                          className={[
                            'block w-full px-3 py-2 text-left text-sm hover:bg-slate-50',
                            customerId === c.id ? 'bg-primary/10 font-semibold text-primary' : '',
                          ].join(' ')}
                          onClick={() => {
                            setCustomerId(c.id);
                            setCustomerSearch(label);
                          }}
                        >
                          {label}
                          {c.phone ? ` · ${c.phone}` : ''}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </>
          )}
        </div>

        <label className="block text-sm font-medium text-slate-700">
          Name
          <input
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Screen Protector Plan"
          />
        </label>

        <label className="block text-sm font-medium text-slate-700">
          Description (optional)
          <textarea
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm font-medium text-slate-700">
            Amount ($)
            <input
              type="number"
              min="0"
              step="0.01"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </label>
          <label className="block text-sm font-medium text-slate-700">
            Frequency
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as SubscriptionFrequency)}
            >
              {FREQUENCIES.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm font-medium text-slate-700">
            Start date
            <input
              type="date"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </label>
          <label className="block text-sm font-medium text-slate-700">
            Next billing date
            <input
              type="date"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={nextBillingDate}
              onChange={(e) => setNextBillingDate(e.target.value)}
            />
          </label>
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={autoCharge} onChange={(e) => setAutoCharge(e.target.checked)} />
          Auto-charge when supported
        </label>

        <label className="block text-sm font-medium text-slate-700">
          Notes (optional)
          <textarea
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>

        <button
          type="submit"
          disabled={mutation.isPending}
          className="w-full rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#164477] disabled:opacity-50"
        >
          {mutation.isPending ? 'Creating…' : 'Create Subscription'}
        </button>
      </form>
    </SlideOver>
  );
}
